import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Trans } from 'react-i18next';
import { useTranslation } from '~/_shared/utils/hooks';
import { getCachedNumberFormatter } from '~/_shared/utils/numberFormatter/numberFormatter';
import {
  getNiceAreaLimit, getNiceDistanceLimit,
} from '~/_shared/utils/unitSystem/niceLimit.helpers';
import { getUnitSymbols } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import {
  AGS_AREA_VALIDATION_ERROR_CODES, AGS_MAX_AREA_IN_SQUARE_MILES, AGS_MAX_POLYGON_VERTICES, AGS_REPORT_MAX_RADIUS_IN_MILES,
} from '~/exportReport/validation/exportReportApiValidation.helpers';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import {
  MetricsDataLayerBannerComponent, metricsDataLayerBannerTextStyle,
} from './metricsDataLayerBanner.component';

const polygonTooLargeTranslationKeys = {
  boundary: 'dataLayersTool.boundaryTooLarge',
  proximity: 'dataLayersTool.driveTimePolygonTooLarge',
  territory: 'dataLayersTool.territoryTooLarge',
} as const;

const formatLimit = (limit: number) => getCachedNumberFormatter({}).format(limit);

type MetricsDataLayerTagAreaTooLargeComponentProps = Readonly<{
  errorCode: string;
  areaType: keyof typeof polygonTooLargeTranslationKeys;
}>;

export const MetricsDataLayerTagAreaTooLargeComponent = ({ errorCode, areaType }: MetricsDataLayerTagAreaTooLargeComponentProps) => {
  const [t] = useTranslation();
  const unitSystem = useMapSettingsSettingsUnitSystemSelector();
  const { unit } = getUnitSymbols(unitSystem);

  const getErrorMessage = () => {
    switch (errorCode) {
      case AGS_AREA_VALIDATION_ERROR_CODES.PolygonTooComplex:
        return {
          headingKey: 'dataLayersTool.areaTooComplex',
          messageKey: 'dataLayersTool.polygonTooComplex',
          values: { maxPoints: formatLimit(AGS_MAX_POLYGON_VERTICES) },
        } as const;
      case AGS_AREA_VALIDATION_ERROR_CODES.PolygonTooLarge:
        return {
          headingKey: 'dataLayersTool.areaTooLarge',
          messageKey: polygonTooLargeTranslationKeys[areaType],
          values: { maxArea: `${formatLimit(getNiceAreaLimit(AGS_MAX_AREA_IN_SQUARE_MILES, unitSystem))} ${unit}²` },
        } as const;
      case AGS_AREA_VALIDATION_ERROR_CODES.RadiusTooLarge:
        return {
          headingKey: 'dataLayersTool.radiusTooLarge',
          messageKey: 'dataLayersTool.driveTimeRadiusTooBig',
          values: { limit: `${formatLimit(getNiceDistanceLimit(AGS_REPORT_MAX_RADIUS_IN_MILES, unitSystem))} ${unit}` },
        } as const;
      default:
        return null;
    }
  };

  const errorMessage = getErrorMessage();

  if (!errorMessage) {
    return null;
  }

  return (
    <MetricsDataLayerBannerComponent
      heading={t(errorMessage.headingKey)}
      icon={faCircleInfo}
    >
      <p css={metricsDataLayerBannerTextStyle}>
        <Trans
          i18nKey={errorMessage.messageKey}
          tOptions={errorMessage.values}
        />
      </p>
    </MetricsDataLayerBannerComponent>
  );
};
